import { useEffect, useRef } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

/**
 * Trap keyboard focus inside the returned container ref. Use on modals,
 * dialogs, drawers — anywhere Tab should cycle within the surface rather
 * than leak back to the page chrome behind it.
 *
 * Stores the previously-focused element and restores it on cleanup, so
 * dismissing the modal returns the user to whatever they were on.
 *
 * @param {boolean} active - turn the trap on/off (default true)
 * @returns {React.MutableRefObject} ref to attach to the trap root
 */
export default function useFocusTrap(active = true) {
  const containerRef = useRef(null)

  useEffect(() => {
    if (!active) return undefined
    const container = containerRef.current
    if (!container) return undefined

    const previouslyFocused = document.activeElement

    // Move focus into the container so the first Tab lands on a sensible
    // target. Prefer an element flagged with `data-autofocus`, else the
    // first focusable descendant, else the container itself.
    const initialTarget =
      container.querySelector('[data-autofocus]') ||
      container.querySelector(FOCUSABLE_SELECTOR) ||
      container
    initialTarget.focus?.({ preventScroll: true })

    const handleKeyDown = (event) => {
      if (event.key !== 'Tab') return
      const focusables = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled') && el.offsetParent !== null,
      )
      if (focusables.length === 0) {
        event.preventDefault()
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    container.addEventListener('keydown', handleKeyDown)
    return () => {
      container.removeEventListener('keydown', handleKeyDown)
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true })
      }
    }
  }, [active])

  return containerRef
}
